// Role detection and permission helpers

/**
 * Check if user has Risk Manager role
 * @param {Array<string>} userRoles - Array of role strings from JWT claims
 * @returns {boolean}
 */
export const isRiskManager = (userRoles = []) => {
  return userRoles.some(role =>
    role.toLowerCase().includes('risk manager') ||
    role.toLowerCase().includes('riskmanager') ||
    role.toLowerCase() === 'manager'
  );
};

/**
 * Check if user has Team Member role (tech team users who run assessments)
 * Also matches "Data Scientist" for backward compat with legacy Huginn roles.
 * @param {Array<string>} userRoles - Array of role strings from JWT claims
 * @returns {boolean}
 */
export const isTeamMember = (userRoles = []) => {
  return userRoles.some(role => {
    const r = role.toLowerCase();
    return r.includes('team member') || r.includes('teammember')
      || r.includes('data scientist') || r.includes('datascientist');
  });
};

/**
 * Check if user has any explicitly granted role (Risk Manager, Team Member, or Data Scientist).
 * Users without a recognized role are not authorized to use the app.
 * @param {Array<string>} userRoles - Array of role strings from JWT claims
 * @returns {boolean}
 */
export const hasAuthorizedRole = (userRoles = []) => {
  return isRiskManager(userRoles) || isTeamMember(userRoles);
};

/**
 * Check if user is a regular user (non-manager)
 * @param {Array<string>} userRoles - Array of role strings from JWT claims
 * @returns {boolean}
 */
export const isRegularUser = (userRoles = []) => {
  return !isRiskManager(userRoles);
};

/**
 * Get display label for role
 * @param {Array<string>} userRoles - Array of role strings from JWT claims
 * @returns {string}
 */
export const getRoleLabel = (userRoles = []) => {
  if (isRiskManager(userRoles)) {
    return 'Risk Manager';
  }
  if (isTeamMember(userRoles)) {
    return 'Team Member';
  }
  return userRoles[0] || 'User';
};

/**
 * Check if user has permission to access a route
 * @param {string} path - Route path
 * @param {Array<string>} userRoles - Array of role strings from JWT claims
 * @returns {boolean}
 */
export const canAccessRoute = (path, userRoles = []) => {
  const managerOnlyRoutes = [
    '/risk-themes',
    '/risk-form-manager',
    '/file-upload',
    '/universal-uploader',
    '/risk-catalogues',
    '/risk-policies',
  ];

  // Risk Managers can access everything
  if (isRiskManager(userRoles)) {
    return true;
  }

  // Regular users cannot access manager-only routes
  return !managerOnlyRoutes.some(route => path.startsWith(route));
};
